import React, { useCallback, useEffect, useMemo, useState } from 'react';
import fabricData from '../../../data/fabricPricingList.json';
import {
  JOURNEY, STAGES, STAGE_ORDER, LOST_REASONS, KEEP_OUT_REASONS, SEGMENTS, journeyIndex, nextStep, dueLabel,
  emailTemplates, whatsappTemplates, whatsappLink, computeQuote, istToday,
} from './deskModel';

const TOOL_FOR_ACTION = { reply: 'email', email: 'email', whatsapp: 'whatsapp', swatch: 'swatch', call: 'call', sample: 'sample', quote: 'quote' };
const TABS = [
  ['email', 'Email'], ['whatsapp', 'WhatsApp'], ['call', 'Call'], ['swatch', 'Swatch kit'], ['sample', 'Sample'], ['quote', 'Quote'],
];
const plusDays = (n) => istToday(new Date(Date.now() + n * 86400000));
const rupees = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

function EmailTool({ lead, onSend, openInbox }) {
  const templates = useMemo(() => emailTemplates(lead), [lead]);
  const [templateId, setTemplateId] = useState(templates[0]?.id);
  const tpl = templates.find((t) => t.id === templateId) || templates[0];
  const [subject, setSubject] = useState(tpl?.subject || '');
  const [body, setBody] = useState(tpl?.body || '');
  const [inThread, setInThread] = useState(Boolean(lead.lastEmailThreadId));
  const [plainText, setPlainText] = useState(['identified', 'contacted', 'winback'].includes(lead.stage));
  const [sending, setSending] = useState(false);
  useEffect(() => { if (tpl) { setSubject(tpl.subject); setBody(tpl.body); } }, [tpl]);

  if (!lead.email) return <p style={{ margin: 0, color: 'var(--bd-muted)' }}>No email address on this lead. Add one with Edit details, or use WhatsApp.</p>;
  const send = async () => {
    setSending(true);
    try { await onSend({ subject, body, inThread, plainText }); } finally { setSending(false); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {lead.stage === 'replied' && lead.linkedThreadId && (
        <div className="bd-pop" style={{ background: 'var(--bd-mari-tint)', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, color: 'oklch(0.3 0.04 60)' }}>The buyer replied in Gmail. Answer inside that conversation with an AI draft.</span>
          <button type="button" className="bd-btn bd-btn--ink" onClick={() => openInbox(lead.linkedThreadId)}>Open thread with AI draft</button>
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} role="group" aria-label="Templates">
        {templates.map((t) => (
          <button key={t.id} type="button" className={`bd-btn bd-btn--sm ${t.id === tpl?.id ? 'bd-btn--primary' : 'bd-btn--ghost'}`} onClick={() => setTemplateId(t.id)}>{t.label}</button>
        ))}
      </div>
      <span style={{ fontSize: 13, color: 'var(--bd-muted)' }}>
        From {lead.market === 'export' ? 'export@' : 'wholesale@'}fabvoguestudio.com to {lead.email} · email {(Number(lead.touchCount) || 0) + 1}
      </span>
      <label className="bd-field">Subject<input className="bd-input" value={subject} onChange={(e) => setSubject(e.target.value)} /></label>
      <label className="bd-field">Message<textarea className="bd-textarea" rows={11} value={body} onChange={(e) => setBody(e.target.value)} /></label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: 'var(--bd-ink-2)' }}>
        {lead.lastEmailThreadId && (
          <label style={{ display: 'flex', gap: 8 }}><input type="checkbox" checked={inThread} onChange={(e) => setInThread(e.target.checked)} />Reply in the previous email thread</label>
        )}
        <label style={{ display: 'flex', gap: 8 }}><input type="checkbox" checked={plainText} onChange={(e) => setPlainText(e.target.checked)} />Plain text, no branded layout (better inbox placement for cold emails)</label>
      </div>
      <div><button type="button" className="bd-btn bd-btn--primary" disabled={sending || !subject.trim() || !body.trim()} onClick={send}>{sending ? 'Sending…' : 'Send email'}</button></div>
    </div>
  );
}

function WhatsAppTool({ lead, onLog }) {
  const templates = whatsappTemplates(lead);
  if (!whatsappLink(lead, '')) return <p style={{ margin: 0, color: 'var(--bd-muted)' }}>No phone number on this lead. Add one with Edit details.</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {templates.map((t) => (
        <div key={t.id} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 14, borderRadius: 12, background: 'var(--bd-sunk)' }}>
          <span className="bd-k">{t.label}</span>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>{t.text}</p>
          <div>
            <a className="bd-btn bd-btn--ghost bd-btn--sm" href={whatsappLink(lead, t.text)} target="_blank" rel="noreferrer" onClick={() => onLog('whatsapp_opened', `WhatsApp: ${t.label}`)}>Open in WhatsApp</a>
          </div>
        </div>
      ))}
      <span style={{ fontSize: 12, color: 'var(--bd-muted)' }}>Sending happens in WhatsApp; the desk records that you opened it.</span>
    </div>
  );
}

function CallTool({ lead, onLog, onSave }) {
  const [note, setNote] = useState('');
  const [nextDate, setNextDate] = useState(plusDays(3));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await onLog('call', note.trim());
      if (nextDate) await onSave({ followUpDate: nextDate }, 'Call saved');
      setNote('');
    } finally { setSaving(false); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {lead.phone && <span style={{ fontSize: 14 }}>Call <a href={`tel:${lead.phone}`} className="bd-mono">{lead.phone}</a></span>}
      <label className="bd-field">What did they say?
        <textarea className="bd-textarea" style={{ minHeight: 110 }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Loved the Modal Satin drape, wants 2 designs printed as a 5 m trial" />
      </label>
      <div className="bd-grid2">
        <label className="bd-field">Next follow-up<input type="date" className="bd-input" value={nextDate} onChange={(e) => setNextDate(e.target.value)} /></label>
      </div>
      <div><button type="button" className="bd-btn bd-btn--primary" disabled={saving || !note.trim()} onClick={save}>Save call</button></div>
    </div>
  );
}

function SwatchTool({ lead, onSave }) {
  const [awb, setAwb] = useState(lead.awb || '');
  const [courier, setCourier] = useState(lead.courier || (lead.market === 'export' ? 'Aramex' : 'DTDC'));
  const [address, setAddress] = useState(lead.address || '');
  const stage = lead.stage;
  const beforeSwatch = ['identified', 'contacted', 'winback', 'replied'].includes(stage);
  return (
    <div className="bd-grid2" style={{ gap: 22 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <span className="bd-k">Kit to pack</span>
        {(lead.fabrics || []).length === 0 ? <span style={{ color: 'var(--bd-muted)' }}>No fabrics picked yet. Choose the niche in Edit details.</span> : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {lead.fabrics.map((f) => (
              <li key={f}><label style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 14 }}><input type="checkbox" />{f}</label></li>
            ))}
          </ul>
        )}
        <span style={{ fontSize: 13, color: 'var(--bd-muted)' }}>A4 cuts, pinked edges, header card with QR. {lead.shippingModel === 'collect' ? 'Freight on the buyer.' : `Complimentary: about ${lead.market === 'export' ? '₹1,300 by Aramex' : '₹120 by DTDC'}.`}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <span className="bd-k">Ship</span>
        {beforeSwatch && (
          <>
            <label className="bd-field">Delivery address<textarea className="bd-textarea" style={{ minHeight: 80 }} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Studio address and phone from the buyer" /></label>
            <button type="button" className="bd-btn bd-btn--primary" disabled={!address.trim()} onClick={() => onSave({ stage: 'swatch_requested', address: address.trim() }, 'Moved to Swatch to pack')}>Buyer wants swatches: add to packing</button>
          </>
        )}
        {(stage === 'swatch_requested' || stage === 'swatch_sent') && (
          <>
            {lead.address && <div style={{ padding: 12, borderRadius: 12, background: 'var(--bd-green-tint)', fontSize: 14, lineHeight: 1.45 }}>{lead.address}</div>}
            <div className="bd-grid2">
              <label className="bd-field">Courier
                <select className="bd-select" value={courier} onChange={(e) => setCourier(e.target.value)}>
                  {['DTDC', 'Delhivery', 'Blue Dart', 'Aramex', 'DHL', 'FedEx', 'UPS', 'Surat Transport Bilty'].map((c) => <option key={c}>{c}</option>)}
                </select>
              </label>
              <label className="bd-field">AWB / tracking<input className="bd-input bd-mono" value={awb} onChange={(e) => setAwb(e.target.value)} /></label>
            </div>
            {stage === 'swatch_requested'
              ? <button type="button" className="bd-btn bd-btn--primary" disabled={!awb.trim()} onClick={() => onSave({ stage: 'swatch_sent', awb: awb.trim(), courier }, 'Swatch dispatched')}>Mark dispatched</button>
              : <button type="button" className="bd-btn bd-btn--primary" onClick={() => onSave({ stage: 'swatch_delivered', awb: awb.trim() || lead.awb, courier }, 'Marked delivered: call within 48 hours')}>Mark delivered</button>}
          </>
        )}
        {!beforeSwatch && !['swatch_requested', 'swatch_sent'].includes(stage) && (
          <span style={{ fontSize: 14, color: 'var(--bd-muted)' }}>{lead.awb ? `Sent by ${lead.courier || 'courier'} · ${lead.awb}${lead.swatchDispatchedAt ? ` on ${lead.swatchDispatchedAt}` : ''}` : 'No swatch on record.'}</span>
        )}
      </div>
    </div>
  );
}

function SampleTool({ lead, onSave, onLog, goQuote }) {
  const [meters, setMeters] = useState('5');
  const [note, setNote] = useState('');
  if (lead.stage !== 'sampling') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ margin: 0, color: 'var(--bd-ink-2)' }}>A paid 3 to 20 m trial is the bridge between swatches and bulk. Record it when the buyer places it.</p>
        <div className="bd-grid2">
          <label className="bd-field">Trial meters<input className="bd-input bd-mono" value={meters} onChange={(e) => setMeters(e.target.value)} /></label>
          <label className="bd-field">Designs / fabric<input className="bd-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. 2 designs on Modal Satin" /></label>
        </div>
        <div><button type="button" className="bd-btn bd-btn--primary" onClick={async () => { await onSave({ stage: 'sampling', targetQty: `${meters} m trial` }, 'Sample order recorded'); if (note.trim()) await onLog('note', `Sample: ${meters} m · ${note.trim()}`); }}>Sample order placed</button></div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ margin: 0, color: 'var(--bd-ink-2)' }}>Trial: {lead.targetQty || 'sample order'}. Ask about colour match, hand-feel, drape and print sharpness.</p>
      <label className="bd-field">Buyer feedback<textarea className="bd-textarea" style={{ minHeight: 90 }} value={note} onChange={(e) => setNote(e.target.value)} /></label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button type="button" className="bd-btn bd-btn--primary" onClick={async () => { await onLog('note', `Sample approved${note.trim() ? `: ${note.trim()}` : ''}`); goQuote(); }}>Approved: make the quote</button>
        <button type="button" className="bd-btn bd-btn--danger" onClick={() => onSave({ stage: 'lost', lostReason: 'quality' }, 'Closed: sample not approved')}>Not approved</button>
      </div>
    </div>
  );
}

function QuoteTool({ lead, onSave, onLog, onUseInEmail }) {
  const fabrics = fabricData?.fabrics || [];
  const isExport = lead.market === 'export';
  const [sku, setSku] = useState((fabrics.find((f) => (lead.fabrics || []).includes(f.fabricName)) || fabrics[4] || fabrics[0] || {}).skuPrefix);
  const [meters, setMeters] = useState(isExport ? 150 : 100);
  const [margin, setMargin] = useState(35);
  const [currency, setCurrency] = useState(isExport ? (lead.country === 'UAE' ? 'AED' : 'USD') : 'INR');
  const [freight, setFreight] = useState(isExport ? 750 : 0);
  const fabric = fabrics.find((f) => f.skuPrefix === sku) || {};
  const q = computeQuote({ fabric, meters, marginPct: margin, currency, freightPerKg: freight });
  const money = (v) => (currency === 'INR' ? rupees(v) : `${currency} ${v}`);
  const text = `Quote for ${lead.brandName}\n${fabric.fabricName} (${fabric.width}), ${meters} m, digital print\n${isExport ? `Ex-mill ${money(q.exMill)}/m, with air freight ${money(q.landed)}/m, total ${money(q.total)}` : `Wholesale ${money(q.exMill)}/m + 5% GST (${rupees(q.gstInclusive)}/m incl.), total ${money(q.total)} + GST`}`;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="bd-grid4">
        <label className="bd-field">Fabric
          <select className="bd-select" value={sku} onChange={(e) => setSku(e.target.value)}>
            {fabrics.map((f) => <option key={f.skuPrefix} value={f.skuPrefix}>{f.fabricName}</option>)}
          </select>
        </label>
        <label className="bd-field">Meters<input type="number" min="1" className="bd-input bd-mono" value={meters} onChange={(e) => setMeters(e.target.value)} /></label>
        <label className="bd-field">Margin %<input type="number" className="bd-input bd-mono" value={margin} onChange={(e) => setMargin(e.target.value)} /></label>
        <label className="bd-field">Currency
          <select className="bd-select" value={currency} onChange={(e) => setCurrency(e.target.value)}>{['INR', 'AED', 'USD', 'EUR', 'GBP'].map((c) => <option key={c}>{c}</option>)}</select>
        </label>
        {isExport && <label className="bd-field">Air freight ₹/kg<input type="number" className="bd-input bd-mono" value={freight} onChange={(e) => setFreight(e.target.value)} /></label>}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 24, paddingTop: 10, borderTop: '1px solid var(--bd-line)' }}>
        <span style={{ color: 'var(--bd-muted)' }}>{isExport ? 'Ex-mill' : 'Wholesale'} <strong className="bd-mono" style={{ color: 'var(--bd-ink)', fontSize: 18 }}>{money(q.exMill)}/m</strong></span>
        {isExport ? <span style={{ color: 'var(--bd-muted)' }}>With freight <strong className="bd-mono" style={{ color: 'var(--bd-ink)', fontSize: 18 }}>{money(q.landed)}/m</strong></span>
          : <span style={{ color: 'var(--bd-muted)' }}>With 5% GST <strong className="bd-mono" style={{ color: 'var(--bd-ink)', fontSize: 18 }}>{rupees(q.gstInclusive)}/m</strong></span>}
        <span style={{ color: 'var(--bd-muted)' }}>Order <strong className="bd-mono" style={{ color: 'var(--bd-ink)', fontSize: 18 }}>{money(q.total)}</strong></span>
      </div>
      <span style={{ fontSize: 12, color: 'var(--bd-muted)' }}>Indicative: confirm cost, exchange rate, freight{isExport ? ', duties' : ''} and dispatch date before sending. Wholesale pricing applies from 99 m per order.</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button type="button" className="bd-btn bd-btn--primary" onClick={async () => { await onSave({ stage: 'quote_sent', targetQty: `${meters} m ${fabric.fabricName}` }, 'Quote saved: chase in 2 days'); await onLog('note', text.replace(/\n/g, ' · ')); }}>Save quote, move to Quote</button>
        <button type="button" className="bd-btn bd-btn--ghost" onClick={() => onUseInEmail(text)}>Put in an email</button>
        <button type="button" className="bd-btn bd-btn--ghost" onClick={() => navigator.clipboard?.writeText(text)}>Copy for WhatsApp</button>
        {lead.stage === 'quote_sent' && <button type="button" className="bd-btn bd-btn--mari" onClick={() => onSave({ stage: 'bulk_client' }, 'Won! Reorder reminder set for 45 days')}>Buyer confirmed bulk order</button>}
      </div>
    </div>
  );
}

export default function LeadPage({ lead, today, team, initialTool, onBack, saveLead, sendEmail, loadActivity, addActivity, openInbox, onEditDetails, onKeepOut, onBringBack, notify }) {
  const step = nextStep(lead, today);
  const [tool, setTool] = useState(TOOL_FOR_ACTION[initialTool] || TOOL_FOR_ACTION[step.tool] || 'email');
  const [activity, setActivity] = useState([]);
  const [emailSeed, setEmailSeed] = useState(0);
  const [snoozeDate, setSnoozeDate] = useState(plusDays(30));
  const [lostReason, setLostReason] = useState('');
  const [panel, setPanel] = useState('');
  const [keepReason, setKeepReason] = useState('');
  const [keepNote, setKeepNote] = useState('');
  const [note, setNote] = useState('');

  const refresh = useCallback(() => loadActivity(lead.id).then(setActivity).catch(() => setActivity([])), [lead.id, loadActivity]);
  useEffect(() => { refresh(); }, [refresh, lead.stage, lead.touchCount, lead.lastReplyAt]);

  const save = async (patch, message) => {
    await saveLead(lead, patch);
    if (message) notify(message);
    refresh();
  };
  const log = async (type, summary) => { await addActivity(lead.id, type, summary); refresh(); };
  const idx = journeyIndex(lead.stage);
  const a = lead.account;
  const due = dueLabel(lead, today);

  return (
    <div className="bd-lead">
      <div><button type="button" className="bd-link" style={{ color: 'var(--bd-indigo)', fontWeight: 600 }} onClick={onBack}>‹ Back</button></div>

      <header style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
            <h1 style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-0.02em' }}>{lead.brandName}</h1>
            {lead.source === 'zoho_customer' && <span className="bd-chip">Zoho buyer</span>}
            {lead.source === 'shopify_company' && <span className="bd-chip">Website wholesale</span>}
            <span className="bd-chip bd-chip--indigo">{lead.market === 'export' ? `Export${lead.country ? ` · ${lead.country}` : ''}` : 'India'}</span>
            {a?.segment && <span className="bd-chip bd-chip--mari">{SEGMENTS[a.segment]?.label || a.segment}</span>}
          </div>
          <span style={{ fontSize: 15, color: 'var(--bd-muted)' }}>
            {[lead.city, lead.country].filter(Boolean).join(', ') || 'Location unknown'}{lead.contactPerson ? ` · ${lead.contactPerson}` : ''}
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 10 }}>
          <label className="bd-field">Owner
            <select className="bd-select" value={lead.owner || ''} onChange={(e) => save({ owner: e.target.value }, e.target.value ? 'Owner updated' : 'Lead unassigned')}>
              <option value="">Unassigned</option>
              {team.map((t) => <option key={t.email} value={t.email}>{t.name}</option>)}
            </select>
          </label>
          <label className="bd-field">Stage
            <select className="bd-select" value={lead.stage || 'identified'} onChange={(e) => {
              const stage = e.target.value;
              if (stage === 'lost') { setPanel('lost'); return; }
              if (['swatch_sent', 'swatch_delivered'].includes(stage) && !lead.awb) { setTool('swatch'); notify('Enter the AWB in Swatch kit first'); return; }
              save({ stage }, `Moved to ${STAGES[stage].label}`);
            }}>
              {STAGE_ORDER.map((s) => <option key={s} value={s}>{STAGES[s].label}</option>)}
            </select>
          </label>
          <button type="button" className="bd-btn bd-btn--ghost" onClick={() => setPanel(panel === 'snooze' ? '' : 'snooze')}>Snooze</button>
          <button type="button" className="bd-btn bd-btn--danger" onClick={() => setPanel(panel === 'lost' ? '' : 'lost')}>Mark lost</button>
          {!lead.keptOut && <button type="button" className="bd-btn bd-btn--ghost" onClick={() => setPanel(panel === 'keepout' ? '' : 'keepout')}>Keep out</button>}
          <button type="button" className="bd-btn bd-btn--ghost" onClick={onEditDetails}>Edit details</button>
        </div>
      </header>

      {panel === 'snooze' && (
        <div className="bd-pop">
          <label className="bd-field">Bring back on<input type="date" className="bd-input" value={snoozeDate} onChange={(e) => setSnoozeDate(e.target.value)} /></label>
          <button type="button" className="bd-btn bd-btn--primary" onClick={async () => { await save({ snoozedUntil: snoozeDate, followUpDate: snoozeDate }, `Snoozed until ${snoozeDate}`); setPanel(''); }}>Snooze</button>
          {lead.snoozedUntil && <button type="button" className="bd-btn bd-btn--ghost" onClick={async () => { await save({ snoozedUntil: '', followUpDate: today }, 'Woken up'); setPanel(''); }}>Wake now</button>}
        </div>
      )}
      {panel === 'lost' && (
        <div className="bd-pop">
          <label className="bd-field">Why was it lost?
            <select className="bd-select" value={lostReason} onChange={(e) => setLostReason(e.target.value)}>
              <option value="" disabled>Pick a reason</option>
              {Object.entries(LOST_REASONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <button type="button" className="bd-btn bd-btn--danger" disabled={!lostReason} onClick={async () => { await save({ stage: 'lost', lostReason }, 'Lead closed as lost'); setPanel(''); }}>Close as lost</button>
          {lostReason === 'unsubscribed' && <span style={{ fontSize: 13, color: 'var(--bd-muted)' }}>The email address goes on the do-not-contact list.</span>}
        </div>
      )}

      {panel === 'keepout' && (
        <div className="bd-pop">
          <label className="bd-field">Why keep this lead out of the desk?
            <select className="bd-select" value={keepReason} onChange={(e) => setKeepReason(e.target.value)}>
              <option value="" disabled>Pick a reason</option>
              {Object.entries(KEEP_OUT_REASONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label className="bd-field" style={{ flex: '1 1 220px' }}>Note (optional)<input className="bd-input" value={keepNote} onChange={(e) => setKeepNote(e.target.value)} placeholder="e.g. buys 2 m for herself" /></label>
          <button type="button" className="bd-btn bd-btn--ink" disabled={!keepReason} onClick={async () => { await onKeepOut([lead.id], keepReason, keepNote); setPanel(''); refresh(); }}>Keep out for good</button>
          <span style={{ flexBasis: '100%', fontSize: 13, color: 'var(--bd-muted)' }}>It leaves Today, the board and Accounts, gets no emails, and the daily sync and CSV imports will not add it again. You can bring it back from Accounts, Kept out.</span>
        </div>
      )}
      {lead.keptOut && (
        <div className="bd-pop" role="status" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14 }}>
            <strong>Kept out of the desk:</strong> {KEEP_OUT_REASONS[lead.keptOutReason] || 'no reason'}{lead.keptOutNote ? ` (${lead.keptOutNote})` : ''}
            <span style={{ color: 'var(--bd-muted)' }}>{lead.keptOutBy ? ` · by ${lead.keptOutBy}` : ''}{lead.keptOutAt ? ` on ${lead.keptOutAt.slice(0, 10)}` : ''}</span>
          </span>
          <button type="button" className="bd-btn bd-btn--primary" onClick={async () => { await onBringBack([lead.id]); refresh(); }}>Bring it back</button>
        </div>
      )}

      <section className="bd-steps" aria-label={`Journey: ${STAGES[lead.stage]?.label}`}>
        {JOURNEY.map((j, i) => (
          <div key={j.key} className={`bd-step ${i < idx ? 'done' : i === idx && lead.stage !== 'lost' ? 'now' : ''}`}>
            <span aria-hidden="true" />
            <strong style={{ fontSize: 13, fontWeight: i <= idx ? 600 : 500, color: i <= idx ? 'var(--bd-ink)' : 'var(--bd-muted)' }}>{j.label}</strong>
          </div>
        ))}
      </section>

      <div className="bd-cols">
        <div>
          {step.cta && !lead.keptOut && (
            <section style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 16, padding: 22, borderRadius: 16, background: 'var(--bd-mari-tint)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: '62ch' }}>
                <span className="bd-k" style={{ color: 'var(--bd-mari-ink)' }}>Next step · {due.text}</span>
                <p style={{ margin: 0, fontSize: 18, lineHeight: 1.4, fontWeight: 600, color: 'oklch(0.26 0.04 60)' }}>{step.text}</p>
              </div>
              <button type="button" className="bd-btn bd-btn--ink bd-btn--lg" onClick={() => {
                if (step.tool === 'wake') { save({ snoozedUntil: '', followUpDate: today }, 'Woken up'); return; }
                if (step.tool === 'reply' && lead.linkedThreadId) { openInbox(lead.linkedThreadId); return; }
                setTool(TOOL_FOR_ACTION[step.tool] || 'email');
              }}>{step.cta}</button>
            </section>
          )}

          <section className="bd-panel" aria-label="Tools">
            <div className="bd-tabs" role="tablist" aria-label="Tools for this lead">
              {TABS.map(([key, label]) => (
                <button key={key} type="button" role="tab" aria-selected={tool === key} onClick={() => setTool(key)}>{label}</button>
              ))}
            </div>
            {tool === 'email' && <EmailTool key={emailSeed} lead={lead} openInbox={openInbox} onSend={async (payload) => { const r = await sendEmail(lead, payload); notify(r?.followUpDate ? `Email sent. Next follow-up ${r.followUpDate}` : 'Email sent'); refresh(); }} />}
            {tool === 'whatsapp' && <WhatsAppTool lead={lead} onLog={log} />}
            {tool === 'call' && <CallTool lead={lead} onLog={log} onSave={save} />}
            {tool === 'swatch' && <SwatchTool lead={lead} onSave={save} />}
            {tool === 'sample' && <SampleTool lead={lead} onSave={save} onLog={log} goQuote={() => setTool('quote')} />}
            {tool === 'quote' && <QuoteTool lead={lead} onSave={save} onLog={log} onUseInEmail={(text) => { navigator.clipboard?.writeText(text); setEmailSeed((n) => n + 1); setTool('email'); notify('Quote copied: paste it into the message'); }} />}
          </section>
        </div>

        <aside>
          {a && (
            <section className="bd-panel" aria-label="Account">
              <span className="bd-k">Account</span>
              <dl className="bd-dl">
                <dt>Lifetime</dt><dd className="bd-mono">{rupees(a.totalSpent)}</dd>
                <dt>Orders</dt><dd className="bd-mono">{a.ordersCount || 0}{a.offlineOrders ? ` (${a.offlineOrders} Zoho)` : ''}</dd>
                <dt>Last order</dt><dd className="bd-mono">{a.lastOrderAt || 'never'}</dd>
                {a.topFabrics?.length > 0 && <><dt>Buys most</dt><dd>{a.topFabrics.join(', ')}</dd></>}
                {a.gstNo && <><dt>GST</dt><dd className="bd-mono">{a.gstNo}</dd></>}
              </dl>
            </section>
          )}
          <section className="bd-panel" aria-label="Contact">
            <span className="bd-k">Contact</span>
            <dl className="bd-dl">
              <dt>Email</dt><dd>{lead.email ? <a href={`mailto:${lead.email}`}>{lead.email}</a> : '—'}</dd>
              <dt>Phone</dt><dd className="bd-mono">{lead.phone || '—'}</dd>
              <dt>Instagram</dt><dd>{lead.instagram ? <a href={`https://instagram.com/${lead.instagram.replace('@', '')}`} target="_blank" rel="noreferrer" onClick={() => log('instagram_opened', 'Instagram opened')}>{lead.instagram}</a> : '—'}</dd>
              <dt>Emails sent</dt><dd className="bd-mono">{Number(lead.touchCount) || 0} of 5</dd>
            </dl>
            <label className="bd-field">Next follow-up
              <input type="date" className="bd-input" value={lead.followUpDate || ''} onChange={(e) => save({ followUpDate: e.target.value }, 'Follow-up date set')} />
            </label>
          </section>
          <section className="bd-panel" aria-label="History">
            <span className="bd-k">History</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="bd-input" aria-label="Add a note" placeholder="Add a note" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && note.trim()) { log('note', note.trim()); setNote(''); } }} />
              <button type="button" className="bd-btn bd-btn--ghost" disabled={!note.trim()} onClick={() => { log('note', note.trim()); setNote(''); }}>Add</button>
            </div>
            {activity.length === 0 ? <span style={{ fontSize: 13, color: 'var(--bd-muted)' }}>No history yet.</span> : (
              <ul className="bd-timeline">
                {activity.map((ev) => (
                  <li key={ev.id}>
                    <span className="bd-dot" style={{ background: ev.type === 'reply_received' ? 'var(--bd-mari)' : ev.type === 'reordered' ? 'oklch(0.52 0.12 155)' : 'var(--bd-indigo)' }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 14, lineHeight: 1.4 }}>{ev.summary}</span>
                      <span className="bd-mono" style={{ fontSize: 11, color: 'var(--bd-muted)' }}>
                        {ev.atIso ? new Date(ev.atIso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : ''}{ev.by && ev.by !== 'system' ? ` · ${ev.by}` : ''}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}
